import React, { useMemo } from "react";
import moment from "moment";
import styles from "../ResourcesPlanner.module.scss";
import { endOfMonth, isWeekendDay, mondayOnOrBefore, startOfMonth } from "../engine/dayMath";
import { DAY_WIDTHS, canvasWidth } from "../engine/timelineGeometry";
import { usePlannerEngine, useView, useViewport } from "../plannerContext";

const MS_PER_DAY = 86400000;
const format = (day, pattern) => moment.utc(day * MS_PER_DAY).format(pattern);

/**
 * The calendar header of the timeline. It is as wide as the whole timeline but holds only the cells of the days
 * and weeks or months that are on screen (plus the window the viewport keeps ready), placed by their position.
 */
function TimelineHeader() {
  const { timeline, today } = usePlannerEngine();
  const viewType = useView((state) => state.viewType);
  const firstDay = useViewport((state) => state.firstDay);
  const lastDay = useViewport((state) => state.lastDay);

  const dayWidth = DAY_WIDTHS[viewType];
  const x = (day) => (day - timeline.originDay) * dayWidth;

  const groups = useMemo(() => {
    const result = [];
    if (viewType === "week") {
      for (let monday = mondayOnOrBefore(firstDay); monday <= lastDay; monday += 7) {
        result.push({ key: monday, left: x(monday), width: 7 * dayWidth, label: `${format(monday, "MMM D")} - ${format(monday + 6, "MMM D YYYY")}` });
      }
    } else if (viewType === "month") {
      for (let first = startOfMonth(firstDay); first <= lastDay; first = endOfMonth(first) + 1) {
        const last = endOfMonth(first);
        result.push({ key: first, left: x(first), width: (last - first + 1) * dayWidth, label: format(first, "MMM YYYY") });
      }
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewType, firstDay, lastDay, dayWidth, timeline]);

  const days = useMemo(() => {
    const result = [];
    for (let day = firstDay; day <= lastDay; day += 1) {
      let name = null;
      if (viewType === "day") name = format(day, "ddd");
      else if (viewType === "week") name = format(day, "dd");

      let className = styles.dayCell;
      if (viewType === "day") className += ` ${styles.dayCellDayView}`;
      if (viewType === "month") className += ` ${styles.dayCellCompact}`;
      if (isWeekendDay(day)) className += ` ${styles.dayCellWeekend}`;
      if (day === today) className += ` ${styles.dayCellToday}`;

      result.push({ day, left: x(day), name, number: format(day, "D"), className });
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewType, firstDay, lastDay, dayWidth, timeline, today]);

  const groupClass = viewType === "week" ? styles.weekCell : styles.monthCell;

  return (
    <div className={styles.headers} style={{ width: canvasWidth(timeline, dayWidth) }}>
      {groups.length > 0 && (
        <div className={`${styles.headerBand} ${styles.headerBandTop}`}>
          {groups.map((group) => (
            <div key={group.key} className={`${styles.headerCell} ${groupClass}`} style={{ left: group.left, width: group.width, padding: 0 }}>
              {group.label}
            </div>
          ))}
        </div>
      )}

      <div className={`${styles.headerBand} ${groups.length > 0 ? styles.headerBandBottom : styles.headerBandFull}`}>
        {days.map((cell) => (
          <div key={cell.day} className={`${styles.headerCell} ${cell.className}`} style={{ left: cell.left, width: dayWidth }}>
            {cell.name && <span className={styles.dayCellName}>{cell.name}</span>}
            <span className={styles.dayCellNumber}>{cell.number}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default React.memo(TimelineHeader);
